import { RefObject, useLayoutEffect } from "react";

/**
 * Sizes a textarea to its content, so no copy sits below the bottom of a box with nothing to
 * say it is there (MPN-1710: 459 characters of body copy in a 4-row box, the hidden part
 * being the mandated-reporter paragraph). The `rows` attribute stays the floor, so copy that
 * already fit keeps the box it always had.
 *
 * Refits when the value changes and when the box changes width, since a narrower box wraps
 * into more lines. A textarea that is not laid out yet (inside something hidden) measures 0;
 * it is left at its rows and fitted when it first gets a width.
 */
export function useAutoGrow(ref: RefObject<HTMLTextAreaElement | null>, value: string, extra = 0) {
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        const fit = () => {
            // Collapsing to measure can shorten the page under a scrolled panel, and the
            // browser clamps its scroll position; put it back once the height is set.
            const scrolled: [Element, number][] = [];
            for (let p = el.parentElement; p; p = p.parentElement) {
                if (p.scrollTop) scrolled.push([p, p.scrollTop]);
            }
            el.style.height = "auto";
            if (!el.scrollHeight) {
                el.style.height = "";
                return;
            }
            const s = getComputedStyle(el);
            const px = (v: string) => parseFloat(v) || 0;
            // scrollHeight counts padding and not border; which of the two `height` includes
            // depends on box-sizing.
            const frame = s.boxSizing === "border-box"
                ? px(s.borderTopWidth) + px(s.borderBottomWidth)
                : -(px(s.paddingTop) + px(s.paddingBottom));
            el.style.height = `${el.scrollHeight + frame + extra}px`;
            for (const [p, top] of scrolled) p.scrollTop = top;
        };

        fit();
        if (typeof ResizeObserver === "undefined") return;
        let width = el.clientWidth;
        const observer = new ResizeObserver(() => {
            // Setting the height fires this too; only a new width can change the fit.
            if (el.clientWidth === width) return;
            width = el.clientWidth;
            fit();
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref, value, extra]);
}
